import { useGetLatestEvaluationQuery } from '@/apis/evaluations/evaluationsApi';
import {
  Button,
  Chip,
  LoadingSpinner,
  Stack,
  TextTruncate,
  Typography,
} from '@data-products-and-ai/react-components';
import { FC, useMemo, useState } from 'react';
import { DataItems } from '@/components';
import { formatDateString } from '@/utils';

import '@/pages/Millwrights/[ID]/components/MillwrightLatestObservation/MillwrightLatestObservation.scss';

interface MillwrightLatestObservationProps {
  millwrightId: number;
  outageId: number;
}

export const MillwrightLatestObservation: FC<
  MillwrightLatestObservationProps
> = ({ millwrightId, outageId }) => {
  const [isOutageEvaluation, setIsOutageEvaluation] = useState(true);
  const {
    data: evaluation,
    isLoading,
    isFetching,
  } = useGetLatestEvaluationQuery(
    { empId: millwrightId, ...(isOutageEvaluation && { outageId }) },
    { skip: !millwrightId },
  );

  const dataItems = useMemo(
    () =>
      evaluation
        ? [
            {
              key: 'evalDate',
              label: 'Date',
              inline: true,
              single: true,
              value: (
                <Typography color="var(--color-black)" tag="textsmall">
                  {evaluation?.evalDate
                    ? formatDateString(evaluation.evalDate, 'MMMM Do, YYYY')
                    : 'N/A'}
                </Typography>
              ),
            },
            {
              key: 'outageId',
              label: 'Outage ID',
              inline: true,
              single: true,
              value: (
                <Typography color="var(--color-black)" tag="textsmall">
                  {evaluation.outageId}
                </Typography>
              ),
            },
            {
              key: 'evaluation',
              label: 'Evaluation',
              inline: true,
              single: true,
              value: (
                <Stack direction="row" gap={8} wrap>
                  <Chip
                    label={`${evaluation?.eval ?? 0}`}
                    size="extra-small"
                    color="primary"
                    chipType="thin"
                  />
                  <Typography color="var(--color-default)" tag="textsmall">
                    out of
                  </Typography>
                  <Typography color="var(--color-black)" tag="textsmall_strong">
                    4
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'notes',
              label: 'Notes',
              value: (
                <TextTruncate width="100%" showMoreText showLessText lines={2}>
                  {!evaluation?.notes ||
                  evaluation.notes === '\n' ||
                  evaluation.notes === '\n\n'
                    ? 'N/A'
                    : evaluation.notes}
                </TextTruncate>
              ),
              single: true,
            },
          ]
        : [],
    [evaluation],
  );

  const handleToggleOutageEvaluation = () =>
    setIsOutageEvaluation(!isOutageEvaluation);

  if (isLoading) {
    return (
      <div className="millwright-latest-observation-loader">
        <LoadingSpinner
          text="Loading evaluation..."
          size={36}
          color="var(--color-primary)"
        />
      </div>
    );
  }

  return (
    <div className="millwright-latest-observation">
      <Typography color="var(--color-neutral)" tag="textdefault_strong">
        Latest evaluation
      </Typography>
      {!evaluation && isOutageEvaluation && (
        <Stack verticalAlign="top">
          <Typography color="var(--color-default)" tag="textsmall">
            There are no evaluations available for this outage. If you would
            like to view the latest personal evaluation for this millwright,
            please click the link above.
          </Typography>
          <Button
            type="link"
            allCaps={false}
            textColor="var(--color-primary)"
            onClick={handleToggleOutageEvaluation}
          >
            <Typography tag="textsmall">Find more</Typography>
          </Button>
        </Stack>
      )}
      {isFetching && (
        <LoadingSpinner
          text="Searching for evaluation..."
          size={36}
          color="var(--color-primary)"
        />
      )}
      {evaluation && <DataItems items={dataItems} />}
    </div>
  );
};
